import { useEffect, useRef, useCallback } from "react";

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * Returns a ref to attach to the modal container.
 * When isActive=true:
 *   - Auto-focuses the first focusable child
 *   - Traps Tab/Shift+Tab within the container
 *   - Calls onEscape when Escape is pressed
 */
export function useFocusTrap(isActive, onEscape) {
  const ref = useRef(null);
  const stableEscape = useCallback(onEscape ?? (() => {}), []);

  useEffect(() => {
    if (!isActive || !ref.current) return;

    const container = ref.current;
    const getFocusable = () => Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS));

    // Auto-focus first element on open
    requestAnimationFrame(() => {
      const els = getFocusable();
      els[0]?.focus();
    });

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        stableEscape();
        return;
      }
      if (e.key !== "Tab") return;

      const els = getFocusable();
      if (els.length === 0) { e.preventDefault(); return; }

      const first = els[0];
      const last = els[els.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first || !container.contains(document.activeElement)) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last || !container.contains(document.activeElement)) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isActive, stableEscape]);

  return ref;
}
